import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { ORDER_OPTIONS_DEFAULTS } from "@/lib/orderOptionsDefaults";
import { deepMerge } from "@/lib/siteDefaults";
import DashboardLayout from "@/components/DashboardLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Save, Plus, Trash2, RotateCcw } from "lucide-react";

const GROUPS = [
  { key: "macro_categories", label: "Tipi di lavoro", hint: "Voci del menu \"Tipo di lavoro\" nel form ordine." },
  { key: "no_material_categories", label: "Tipi di lavoro senza materiale", hint: "Per questi tipi il campo \"Materiale\" non viene mostrato (es. Progettazione). Devono coincidere con voci presenti nei Tipi di lavoro." },
  { key: "materials", label: "Materiali / Lavorazioni", hint: "Voci del menu \"Materiale\" (le lavorazioni di stampa)." },
  { key: "quantities", label: "Quantità (per Stampa 3D)", hint: "Voci del menu \"Quantità\", mostrato insieme al materiale." },
  { key: "design_types", label: "Tipo di lavoro (per Progettazione)", hint: "Voci del menu mostrato per i tipi di lavoro senza materiale (es. Progettazione)." },
  { key: "colors", label: "Colori (scala VITA)", hint: "Voci del menu \"Colore\"." },
];

const DEADLINE_FIELDS = [
  { key: "stampa_3d", label: "Stampa 3D", def: 1 },
  { key: "progettazione_1_4", label: "Progettazione · 1-4 elementi", def: 2 },
  { key: "progettazione_5_plus", label: "Progettazione · 5+ elementi", def: 3 },
  { key: "toronto", label: "Toronto tecnica DUO", def: 5 },
];

function DeadlineEditor({ values, onChange }) {
  const v = values || {};
  return (
    <Card className="p-6 border-slate-200 lg:col-span-2" data-testid="options-group-deadline_days">
      <div className="mb-4">
        <h3 className="font-heading text-lg font-semibold text-slate-900">Scadenza minima (giorni lavorativi)</h3>
        <p className="text-sm text-slate-500 mt-1">Giorni lavorativi minimi (esclusi sabato e domenica) tra la data di inserimento dell'ordine e la scadenza scelta dal cliente, in base al tipo di lavoro.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {DEADLINE_FIELDS.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">{f.label}</label>
            <Input
              type="number"
              min={0}
              data-testid={`deadline-input-${f.key}`}
              value={v[f.key] ?? f.def}
              onChange={(e) => onChange({ ...v, [f.key]: e.target.value === "" ? "" : Number(e.target.value) })}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}


function ListEditor({ group, values, onChange }) {
  return (
    <Card className="p-6 border-slate-200" data-testid={`options-group-${group.key}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">{group.label}</h3>
          <p className="text-sm text-slate-500 mt-1">{group.hint}</p>
        </div>
        <span className="text-xs font-bold text-slate-400 shrink-0">{(values || []).length} voci</span>
      </div>
      <div className="space-y-2">
        {(values || []).map((v, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <Input
              data-testid={`option-input-${group.key}-${idx}`}
              value={v}
              onChange={(e) => { const arr = [...values]; arr[idx] = e.target.value; onChange(arr); }}
            />
            <Button type="button" variant="ghost" size="sm" className="text-rose-500 shrink-0"
              data-testid={`option-remove-${group.key}-${idx}`}
              onClick={() => onChange(values.filter((_, i) => i !== idx))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" data-testid={`option-add-${group.key}`}
          onClick={() => onChange([...(values || []), ""])}>
          <Plus className="h-4 w-4 mr-1.5" /> Aggiungi voce
        </Button>
      </div>
    </Card>
  );
}

export default function OrderOptionsPage() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/order-options")
      .then(({ data }) => setData(deepMerge(ORDER_OPTIONS_DEFAULTS, data || {})))
      .catch(() => setData(JSON.parse(JSON.stringify(ORDER_OPTIONS_DEFAULTS))));
  }, []);

  const setGroup = (key, arr) => setData((d) => ({ ...d, [key]: arr }));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = {};
      Object.keys(data).forEach((k) => {
        cleaned[k] = Array.isArray(data[k]) ? data[k].map((s) => (s || "").trim()).filter(Boolean) : data[k];
      });
      cleaned.deadline_days = {};
      DEADLINE_FIELDS.forEach((f) => {
        const n = parseInt((data.deadline_days || {})[f.key], 10);
        cleaned.deadline_days[f.key] = isNaN(n) || n < 0 ? f.def : n;
      });
      await api.put("/order-options", { options: cleaned });
      setData(cleaned);
      toast.success("Opzioni salvate. Il form ordine è aggiornato.");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setData(JSON.parse(JSON.stringify(ORDER_OPTIONS_DEFAULTS)));
    toast.info("Valori riportati agli originali (ricordati di salvare).");
  };

  if (!data) {
    return (
      <DashboardLayout title="Opzioni Ordini" subtitle="Gestisci tipi di lavoro, materiali e colori del form ordine.">
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Opzioni Ordini"
      subtitle="Gestisci le voci del form ordine: tipi di lavoro, materiali/lavorazioni e colori."
      actions={
        <Button data-testid="save-order-options" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Salva
        </Button>
      }
    >
      <div className="flex justify-end mb-4">
        <Button type="button" variant="ghost" size="sm" onClick={resetDefaults} className="text-slate-500">
          <RotateCcw className="h-4 w-4 mr-1.5" /> Ripristina valori originali
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DeadlineEditor values={data.deadline_days} onChange={(obj) => setData((d) => ({ ...d, deadline_days: obj }))} />
        {GROUPS.map((g) => (
          <ListEditor key={g.key} group={g} values={data[g.key]} onChange={(arr) => setGroup(g.key, arr)} />
        ))}
      </div>
      <div className="mt-8">
        <Button data-testid="save-order-options-bottom" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Salva modifiche
        </Button>
      </div>
    </DashboardLayout>
  );
}
